/// <amd-module name="Repos/Views/PullRequestListingPage/PullRequestListingService" />
import { GitPullRequest, GitPullRequestSearchCriteria, GitVersionDescriptor, PullRequestStatus } from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { branchNameToRef } from "Repos/Common/Util/Ref";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { IPullRequest, IPullRequestSection } from "Repos/PullRequestList/Contracts";
import { IPullRequestDataProviderService } from "Repos/PullRequestList/PullRequestDataProviderService";
import { convertPullRequest } from "Repos/PullRequestList/Util";
import * as Resources from "Repos/Views/PullRequestListingPage/Resources";
import { IObservableValue, IReadyableReadonlyObservableArray, ObservableArray, ObservableValue, ReadyableObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageService } from "VSS/Platform/Page";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { FILTER_CHANGE_EVENT, IFilter, IFilterState } from "VSSUI/Utilities/Filter";
import * as React from "react";
import { getGitClient } from "./RestClient/Git";

export interface IPullRequestListingView {
    sections: IObservableValue<IReadyableReadonlyObservableArray<IPullRequestSection>>;
}

interface IClientSectionsView {
    processedPrIds: Set<number>;
    sections: ReadyableObservableArray<IPullRequestSection>;
    skip: number;
    viewStateId: number;
    isLoading: boolean;
}

export interface IPullRequestListingService extends IVssService {
    getPullRequestsView(selectedTabId: ObservableValue<string>, filter: IFilter): IPullRequestListingView;
    navigateToPullRequest(id: string): void;
}

type TabName = "mine" | "active" | "completed" | "abandoned";

class PullRequestListingService extends VssService implements IPullRequestListingService {
    private personaProvider: PersonaDetailsProvider;
    private prDataProviderService: IPullRequestDataProviderService;
    private subscriptionCleanups: (() => void)[] = [];
    private fixesEnabled: boolean;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.personaProvider = new PersonaDetailsProvider(pageContext);
        this.prDataProviderService = pageContext.getService<IPullRequestDataProviderService>("IPullRequestDataProviderService");
        this.fixesEnabled = isFeatureFlagEnabled(pageContext, "SourceControl.GitPullRequests.FixPRListPagination");
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);
        for (const cleanup of this.subscriptionCleanups) {
            cleanup();
        }
    }

    public getPullRequestsView(selectedTabId: ObservableValue<TabName>, filter: IFilter): IPullRequestListingView {
        const sections = new ObservableValue<IReadyableReadonlyObservableArray<IPullRequestSection>>(
            new ReadyableObservableArray<IPullRequestSection>([])
        );

        const clientSectionsView: IClientSectionsView = {
            processedPrIds: new Set(),
            sections: new ReadyableObservableArray(),
            skip: 0,
            viewStateId: 0,
            isLoading: false
        };

        // We don't allow an active filter on the mine tab, so we switch to active
        // when the filter changes, or clear the filter when we switch to mine.
        // Normally we update the sections when either of these changes happen, so
        // we use the skipProcessing lock boolean to make sure that the side effect change
        // doesn't actually cause us to update the sections a second time.

        let skipProcessing = false;
        const tabIdSubscription = (newTabId: TabName) => {
            if (!skipProcessing) {
                this.resetView(clientSectionsView);

                if (newTabId === "mine") {
                    skipProcessing = true;
                    filter.reset();
                    sections.value = this.prDataProviderService.getPullRequestSections(true);
                    skipProcessing = false;
                } else {
                    sections.value = clientSectionsView.sections;
                    this.loadData(clientSectionsView, newTabId, filter);
                }
            }
        };

        selectedTabId.subscribe(tabIdSubscription);
        this.subscriptionCleanups.push(() => selectedTabId.unsubscribe(tabIdSubscription));

        const filterChangedSubscription = (newFilterState: IFilterState) => {
            if (newFilterState["pullRequestId"]) {
                return;
            }
            if (!skipProcessing) {
                this.resetView(clientSectionsView);

                if (selectedTabId.value === "mine") {
                    skipProcessing = true;
                    selectedTabId.value = "active";
                }
                sections.value = clientSectionsView.sections;
                this.loadData(clientSectionsView, selectedTabId.value, filter);
                skipProcessing = false;
            }
        };

        filter.subscribe(filterChangedSubscription, FILTER_CHANGE_EVENT);
        this.subscriptionCleanups.push(() => filter.unsubscribe(filterChangedSubscription));

        if (selectedTabId.value === "mine") {
            sections.value = this.prDataProviderService.getPullRequestSections(false);
        } else {
            sections.value = clientSectionsView.sections;
            this.loadData(clientSectionsView, selectedTabId.value, filter);
        }

        return { sections };
    }

    public navigateToPullRequest(id: string) {
        const gitRestClient = getGitClient(this.pageContext);
        const gitRepository = getGitRepository(this.pageContext)!;
        const prId = parseInt(id);
        if (isNaN(prId)) {
            this.showNavigationErrorMessage(Resources.PullRequestIdIsInvalid);
        } else {
            gitRestClient.getPullRequest(gitRepository.id, prId).then(
                () => {
                    const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
                    const prUrl = reposRoutingService.getPullRequestUrl(gitRepository.id, gitRepository.project.id, prId);
                    FastPageSwitch(this.pageContext, prUrl, true);
                },
                () => {
                    this.showNavigationErrorMessage(format(Resources.PullRequestIdDoesNotExist, prId));
                }
            );
        }
    }

    private getArtifactDataForPullRequests(prs: IPullRequest[]) {
        if (prs.length && userHasClaim(this.pageContext, UserClaims.Member)) {
            const prDataProviderService = this.pageContext.getService<IPullRequestDataProviderService>("IPullRequestDataProviderService");
            prDataProviderService.updatePullRequestsWithStats(prs);
        }
    }

    private getStatusFromTabId(tabId: TabName) {
        switch (tabId) {
            case "completed":
                return PullRequestStatus.Completed;
            case "abandoned":
                return PullRequestStatus.Abandoned;
            case "active":
            default:
                return PullRequestStatus.Active;
        }
    }

    private resetView(view: IClientSectionsView) {
        view.skip = 0;
        view.processedPrIds.clear();
        view.viewStateId++;
        view.sections.removeAll();
        view.sections.ready.value = false;
        if (this.fixesEnabled) view.isLoading = false;
    }

    private showNavigationErrorMessage(message: string) {
        const messagesService = this.pageContext.getService<IReposMessagesService>("IReposMessagesService");
        messagesService.showMessage({
            renderMessage: () => {
                return <span>{message}</span>;
            },
            severity: MessageCardSeverity.Error
        });
    }

    private loadData(view: IClientSectionsView, selectedTabId: TabName, filter: IFilter): void {
        if (this.fixesEnabled && view.isLoading) return;

        const viewStateId = view.viewStateId;
        const gitRestClient = getGitClient(this.pageContext);
        const gitRepository = getGitRepository(this.pageContext)!;
        const currentUserId = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!.user.id;
        const createdBy = filter.getFilterItemValue<IIdentity>("createdBy");
        const assignedTo = filter.getFilterItemValue<IIdentity>("assignedTo");
        const targetRefName = filter.getFilterItemValue<GitVersionDescriptor | string>("targetRefName");
        const top = 50;

        if (this.fixesEnabled) view.isLoading = true;
        gitRestClient
            .getPullRequests(
                gitRepository.id,
                {
                    creatorId: createdBy && createdBy.localId,
                    reviewerId: assignedTo && assignedTo.localId,
                    status: this.getStatusFromTabId(selectedTabId),
                    targetRefName: targetRefName
                        ? branchNameToRef(
                              (targetRefName as GitVersionDescriptor).version
                                  ? (targetRefName as GitVersionDescriptor).version
                                  : (targetRefName as string)
                          )
                        : undefined
                } as GitPullRequestSearchCriteria,
                gitRepository.project.id,
                undefined,
                view.skip,
                top
            )
            .then((prs: GitPullRequest[]) => {
                if (view.viewStateId === viewStateId) {
                    const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
                    if (prs && prs.length) {
                        const convertedPrs = prs.map(pr => convertPullRequest(pr, reposRoutingService, currentUserId, this.personaProvider));
                        if (view.sections.length === 0) {
                            view.sections.push({
                                countText: new ObservableValue(""),
                                customizable: false,
                                hasMoreResults: new ObservableValue(false),
                                loadMore: () => this.loadData(view, selectedTabId, filter),
                                pullRequests: new ObservableArray<IPullRequest>([...convertedPrs]),
                                showFlagAndDeclineMenuItems: false,
                                title: ""
                            });

                            view.processedPrIds = new Set(convertedPrs.map(pr => pr.id));

                            this.getArtifactDataForPullRequests(convertedPrs);
                        } else {
                            // Remove the last row as it is the loading row.
                            const section = view.sections.value[0];
                            section.pullRequests.pop();

                            const newPrs: IPullRequest[] = [];
                            for (const pr of convertedPrs) {
                                if (this.fixesEnabled) {
                                    // Don't add the PR if it is already in the list.
                                    if (!view.processedPrIds.has(pr.id)) {
                                        view.processedPrIds.add(pr.id);
                                        newPrs.push(pr);
                                    }
                                } else {
                                    // Don't add the PR if it is already in the list, or if
                                    // its id is greater than the current last id in the list,
                                    // because it would show up out of order. This should only
                                    // happen if there are more than $top PRs that are in the
                                    // dataset, from the last call.
                                    if (!view.processedPrIds.has(pr.id) && pr.id < section.lastPullRequestId!) {
                                        view.processedPrIds.add(pr.id);
                                        newPrs.push(pr);
                                    }
                                }
                            }

                            section.pullRequests.push(...newPrs);

                            this.getArtifactDataForPullRequests(newPrs);
                        }

                        view.sections.value[0].lastPullRequestId = prs[prs.length - 1].pullRequestId;

                        if (convertedPrs.length === top) {
                            // Anytime we returned at number of rows that we asked for, we need to add a loading row
                            view.sections.value[0].pullRequests.push(new ObservableValue<IPullRequest | undefined>(undefined));
                            view.sections.value[0].hasMoreResults.value = true;
                            view.skip = view.skip + top;
                        } else {
                            view.sections.value[0].hasMoreResults.value = false;
                        }
                    }
                    view.sections.ready.value = true;
                }
            })
            .catch(reason => {
                throw new Error(reason);
            })
            .finally(() => {
                if (this.fixesEnabled && view.viewStateId === viewStateId)
                    view.isLoading = false;
            });
    }
}

Services.add("IPullRequestListingService", { serviceFactory: PullRequestListingService });
